import { DownOutlined } from '@ant-design/icons';
import { Bar, BarConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import {
  GET_IDEAS_COUNT_BY_REVIEWERS,
  IdeaCountByReviewers,
  IdeaCountByReviewersChartData,
} from '@frontend/data-access';
import { Button, Card, Dropdown } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

const sortData = (data: IdeaCountByReviewers[], sortKey: string) => {
  return [...data].sort((a, b) => {
    const totalA = a.completed + a.pending;
    const totalB = b.completed + b.pending;
    return sortKey === 'HL' ? totalB - totalA : totalA - totalB;
  });
};

function IdeasAssignedToReviewers({
  channelId,
  challengeId,
  innovationSpaceId,
}: {
  channelId?: string;
  challengeId?: string;
  innovationSpaceId?: string;
}) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<IdeaCountByReviewersChartData[]>([]);
  const [getIdeaAssignedCount, { data: rawData, loading }] = useLazyQuery<{
    channelIdeaEvaluationStatistics: IdeaCountByReviewers[];
  }>(GET_IDEAS_COUNT_BY_REVIEWERS);

  const [sortOption, setSortOption] = useState({
    key: 'HL',
    label: t('highest_to_lowest'),
  });

  const sortOptions = useMemo(
    () => [
      { key: 'HL', label: t('highest_to_lowest') },
      { key: 'LH', label: t('lowest_to_highest') },
    ],
    [t]
  );

  useEffect(() => {
    if (!channelId) return;
    getIdeaAssignedCount({
      variables: {
        channelId,
        challengeId,
        spaceId: innovationSpaceId,
      },
    });
  }, [channelId, challengeId, innovationSpaceId, getIdeaAssignedCount]);

  useEffect(() => {
    const newData: IdeaCountByReviewersChartData[] = [];
    const sortedData = sortData(
      rawData?.channelIdeaEvaluationStatistics || [],
      sortOption.key
    );

    if (sortedData.length > 0) {
      // If we have data, process it normally
      for (const item of sortedData) {
        newData.push({
          name: item.user.fullName,
          count: item.completed,
          state: 'Completed',
        });
        newData.push({
          name: item.user.fullName,
          count: item.pending,
          state: 'Pending',
        });
      }
    } else {
      // If no data, add placeholder data
      newData.push({
        name: t('no_data'),
        count: 0,
        state: 'Completed',
      });
      newData.push({
        name: t('no_data'),
        count: 0,
        state: 'Pending',
      });
    }

    setData(newData);
  }, [rawData?.channelIdeaEvaluationStatistics, sortOption, t]);

  const config: BarConfig = {
    data,
    label: false,
    stack: true,
    loading: loading,
    xField: 'name',
    yField: 'count',
    colorField: 'state',
    scale: {
      color: {
        range: ['#47DA47', '#B7B7B7'],
      },
    },
    legend: {
      color: {
        position: 'bottom',
        layout: {
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
    axis: {
      x: {
        label: {
          style: {
            fontFamily: 'Lufga',
            fill: 'rgba(0, 0, 0, 0.65)',
            textAlign: 'right',
          },
        },
        labelFormatter: (value: string) => {
          const str = value.toString();
          if (str.length > 30) {
            return str.slice(0, 30) + '...';
          }
          return str;
        },
      },
      y: {
        min: 0,
        max: data.some((item) => item.count > 0) ? undefined : 1,
      },
    },
  };

  return (
    <Card style={{ borderRadius: 20 }}>
      <div className="space-y-4">
        <div className="flex flex-wrap justify-between items-center">
          <div className="text-sm text-gray-900 font-medium">
            {t('reviewers_experts_status')}
          </div>
          <Dropdown
            menu={{
              items: sortOptions.map((item) => ({
                ...item,
                onClick: () => setSortOption(item),
              })),
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button shape="round">
              {sortOption.label}
              <DownOutlined />
            </Button>
          </Dropdown>
        </div>
        <Bar {...config} />
      </div>
    </Card>
  );
}
export default IdeasAssignedToReviewers;
